import { Injectable, inject, signal, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { endpoints } from '../../../../environments/endpoints';
import { ApiService } from './api.service';
import { LocalStorageService } from './local-storage.service';
import { Subscription } from 'rxjs';

export type Language = 'en' | 'fr';

export interface LanguageOption {
    code: Language;
    label: string;
}

@Injectable({
    providedIn: 'root'
})
export class TranslationService implements OnDestroy {
    private translateService = inject(TranslateService);
    private authService = inject(AuthService);
    private apiService = inject(ApiService);
    private localStorageService = inject(LocalStorageService);
    private userSubscription?: Subscription;
    private readonly LANGUAGE_KEY = 'preferred_lang';

    public currentLanguage = signal<Language>('en');
    public readonly availableLanguages: LanguageOption[] = [
        { code: 'en', label: 'English' },
        { code: 'fr', label: 'Français' }
    ];

    constructor() {
        this.initLanguage();
        this.subscribeToUserChanges();
    }

    ngOnDestroy(): void {
        this.userSubscription?.unsubscribe();
    }

    private initLanguage(): void {
        this.authService.currentUser$.subscribe(user => {
            if (user?.preferred_lang) {
                const userLang = user.preferred_lang as Language;
                if (this.isValidLanguage(userLang)) {
                    this.setLanguage(userLang, false);
                    return;
                }
            }

            const storedLang = this.localStorageService.getItem(this.LANGUAGE_KEY) as Language | null;
            if (storedLang && this.isValidLanguage(storedLang)) {
                this.setLanguage(storedLang, false);
                return;
            }

            const browserLang = this.translateService.getBrowserLang();
            const fallbackLang = browserLang?.startsWith('fr') ? 'fr' : 'en';
            this.setLanguage(fallbackLang, false);
        }).unsubscribe();
    }

    private subscribeToUserChanges(): void {
        this.userSubscription = this.authService.currentUser$.subscribe(user => {
            if (user?.preferred_lang) {
                const userLang = user.preferred_lang as Language;
                if (this.isValidLanguage(userLang) && userLang !== this.currentLanguage()) {
                    this.setLanguage(userLang, false);
                }
            }
        });
    }

    setLanguage(lang: Language, updateBackend: boolean = true): void {
        this.translateService.use(lang).subscribe({
            next: () => {
                this.currentLanguage.set(lang);
                this.localStorageService.setItem(this.LANGUAGE_KEY, lang);

                if (updateBackend && this.authService.isAuthenticated()) {
                    this.apiService.runAndForget(
                        this.apiService.put(endpoints.api.users.updateMe, { body: { preferred_lang: lang }, skipLoading: true })
                    );
                }
            },
        });
    }

    toggleLanguage(): void {
        const newLang: Language = this.currentLanguage() === 'en' ? 'fr' : 'en';
        this.setLanguage(newLang);
    }

    getLanguage(): Language {
        return this.currentLanguage();
    }

    instant(key: string | string[], interpolateParams?: object): string {
        return this.translateService.instant(key, interpolateParams);
    }

    private isValidLanguage(lang: string): lang is Language {
        return lang === 'en' || lang === 'fr';
    }
}